import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { managementApiRef } from '@internal/plugin-management';
import { selectHomeFavoriteLinks } from '../homeFavorites';
import {
  HOME_FAVORITE_LIMIT_DESKTOP,
  HOME_FAVORITE_LIMIT_MOBILE,
} from '../homeUtils';
import type { FavoriteLink } from '@internal/plugin-management-common';
import { useIsDesktopSize } from '../../../hooks/useIsDesktopSize';

export function useHomeFavoriteLinks(): {
  links: FavoriteLink[];
  hasMore: boolean;
} {
  const isDesktop = useIsDesktopSize();

  const limit = isDesktop
    ? HOME_FAVORITE_LIMIT_DESKTOP
    : HOME_FAVORITE_LIMIT_MOBILE;

  const managementApi = useApi(managementApiRef);
  const [favoriteLinks, setFavoriteLinks] = useState<FavoriteLink[]>([]);

  const fetchFavoriteLinks = useCallback(async () => {
    try {
      setFavoriteLinks(await managementApi.listFavoriteLinks());
    } catch (error) {
      console.error('Error fetching home favorite links:', error);
      setFavoriteLinks([]);
    }
  }, [managementApi]);

  useEffect(() => {
    void fetchFavoriteLinks();
    const interval = window.setInterval(fetchFavoriteLinks, 45000);
    window.addEventListener('focus', fetchFavoriteLinks);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', fetchFavoriteLinks);
    };
  }, [fetchFavoriteLinks]);

  return {
    links: useMemo(
      () => selectHomeFavoriteLinks(favoriteLinks, limit),
      [favoriteLinks, limit],
    ),
    hasMore: favoriteLinks.length > limit,
  };
}
